import React from 'react';
import { PsychiatryNoteSection } from '../components/PsychiatryNoteSection.component';
import { Box, FormHelperText, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ProviderPrimaryCptCodeAutocomplete, groupCptCodes } from '../components/CptCodeAutocomplete.component';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole } from '@uplift/types';
import { Button } from '../../../Button';
import { Add } from '@mui/icons-material';
import { useSessionSuggestedCPTCodesQuery, useProviderCptCodesQuery } from '@uplift/queries';
import { Modal, ModalContent } from '../../../Modal';
import { useToggle } from '../../../hooks';
import { AutocompleteRaw } from '../../../Inputs';
import { useField, useFormikContext } from 'formik';
import { Chip } from '../../../Chip';
import { CPTCode, CPTCodeCodingType } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { TipsAndUpdatesOutlinedIcon } from '@uplift/icons';
import { ProgressNoteAlert } from '../components/ProgressNoteAlert.component';


const AddonCPTCodesChipList: React.VFC<{
  codes: CPTCode[];
  suggestions: Record<string, { message: string; count: number }>;
  onDelete: (index: number) => void;
}> = ({ codes, suggestions, onDelete }) => {
  const { classes } = useStyles();
  const renderingSelectedCptCodes = React.useMemo(() => {
    const sugCount = Object.keys(suggestions).reduce((prev, curr) => ({ ...prev, [curr]: suggestions[curr].count }), {} as Record<string, number>);

    return codes.map(c => {
      sugCount[c.code] -= 1;
      return {
        cpt: c,
        isSuggested: sugCount[c.code] >= 0
      };
    });
  }, [ codes, suggestions ]);

  return (
    <Box display="flex" gap="16px" flexWrap="wrap">
      {renderingSelectedCptCodes?.map(({ cpt, isSuggested }, index) => {
        return (
          <Chip
            key={cpt.id + index}
            square
            label={`${cpt.code}: ${cpt.name}`}
            onDelete={() => {
              onDelete(index);
            }}
            classes={isSuggested ? { root: classes.suggestedChip, deleteIcon: classes.suggestedChipIcon } : undefined}
          />
        );
      })}
    </Box>
  );
};


const useStyles = makeStyles()(theme => ({
  suggestedIcon: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,

    borderRadius: '50%',
    padding: '4px',
    marginRight: theme.spacing(1),
  },
  suggestedChip: {
    backgroundColor: theme.palette.info.background,
    color: theme.palette.info.darkText,
    border: `1px solid ${theme.palette.info.main}`,

    '&:focus': {
      backgroundColor: theme.palette.info.background,
    }
  },
  suggestedChipIcon: {
    color: theme.palette.info.main,

    '&:hover': {
      color: theme.palette.info.dark,
    }
  }
}));


export type CPTCodesSectionProps = {
  sessionId: string;
};

export type NoteType = 'clinicalNote' | 'psychiatryNote';
const allowedCptCodingTypeForNote: Record<NoteType, Set<CPTCodeCodingType>> = {
  clinicalNote: new Set([ 'addon', 'hybrid' ]),
  psychiatryNote: new Set([ 'addon', 'hybrid' ]),
};

const useFilterOutCodes = (providerId: string, primaryCptCodeId?: string) => {
  const { data: { cptCodes = [], filterOutRules = [] } = {} } = useProviderCptCodesQuery(providerId);

  return React.useMemo(() => {
    const primaryCode = cptCodes.find(c => c.id === primaryCptCodeId);

    return new Set(filterOutRules?.filter(r => r.ifSelected === primaryCode?.code).map(r => r.filterOut) ?? []);
  }, [ cptCodes, filterOutRules, primaryCptCodeId ]);
};


const getCptCodeLabel = (code?: CPTCode) => `${code?.code}: ${code?.name}`;

export const AddonCptCodesField: React.VFC<{
  name: string;
  providerId: string;
  primaryCptCodeId?: string;
  sessionId?: string;
  noteType?: NoteType;
  buttonTextVariant?: boolean;
}> = ({ name, providerId, sessionId, primaryCptCodeId, noteType = 'psychiatryNote', buttonTextVariant }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const [ field, _meta, helpers ] = useField<string[]>(name);
  const [ inputValue, setInputValue ] = React.useState('');
  const [ open, handleOpen, handleClose ] = useToggle(false);
  const { data: { cptCodes = [] } = {} } = useProviderCptCodesQuery(providerId);
  const cptById = React.useMemo(() => {
    return cptCodes.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {} as Record<string, CPTCode>);
  }, [ cptCodes ]);
  const selectedCptCodes = React.useMemo(() => field.value.map(v => cptById?.[v]).filter(v => Boolean(v)), [ cptById, field.value ]);
  const { data: _allCptSuggestions = {} } = useSessionSuggestedCPTCodesQuery(sessionId);
  const filterOutCodes = useFilterOutCodes(providerId, primaryCptCodeId);
  const allCptSuggestions = React.useMemo(() => {
    const allCptSuggestions = { ..._allCptSuggestions };

    filterOutCodes.forEach(code => {
      delete allCptSuggestions[code];
    });

    return allCptSuggestions;
  }, [ _allCptSuggestions, filterOutCodes ]);
  const cptSuggestions = React.useMemo(() => {
    const _cptSuggestions = { ...allCptSuggestions };

    selectedCptCodes.forEach(({ code }) => {
      if (_cptSuggestions[code]) {
        _cptSuggestions[code] = {
          ..._cptSuggestions[code],
          count: _cptSuggestions[code].count - 1
        };

        if (_cptSuggestions[code].count < 1) {
          delete _cptSuggestions[code];
        }
      }
    });

    return _cptSuggestions;
  }, [ selectedCptCodes, allCptSuggestions ]);

  const codes = React.useMemo(() => {
    const allowedCodes = cptCodes.filter(code => allowedCptCodingTypeForNote[noteType].has(code.codingType));
    const allowedCombinationCodes = filterOutCodes.size ? allowedCodes.filter(c => {
      return !filterOutCodes.has(c.code);
    }) : allowedCodes;
    const grouped = groupCptCodes(allowedCombinationCodes, {}, cptSuggestions);

    return [
      ...(grouped.suggested ?? []),
      ...(grouped.mostCommon ?? []),
      ...(grouped.other ?? [])
    ];
  }, [ cptCodes, cptSuggestions, noteType, filterOutCodes ]);

  const getGroupKey = (option: CPTCode) => {
    if (option.code in cptSuggestions) {
      return l10n.clinicalNotes.suggestedCptCodes;
    }

    return l10n.clinicalNotes.otherCptCodes;
  };

  return (
    <Box>
      {!!selectedCptCodes?.length && (
        <Box pb={2}>
          <AddonCPTCodesChipList
            codes={selectedCptCodes}
            suggestions={allCptSuggestions}
            onDelete={(index) => {
              const newValues = [ ...(field?.value ?? []) ];

              newValues.splice(index, 1);
              helpers.setValue(newValues);
            }}
          />
        </Box>
      )}
      <Button
        disabled={field?.value?.length >= 4}
        onClick={handleOpen}
        color="secondary"
        startIcon={<Add />}
        size="small"
        variant={
          field?.value?.length || buttonTextVariant
            ? 'text'
            : 'contained'
        }
      >
        Include Add-on cpt codes
      </Button>
      <FormHelperText>Include up to 4 add-on codes</FormHelperText>
      <Modal fullWidth maxWidth="md" open={open} onClose={handleClose} keepMounted={false}>
        <ModalContent>
          <Box height="500px">
            <AutocompleteRaw
              autoFocus
              name={name}
              options={codes}
              getOptionLabel={getCptCodeLabel}
              renderOption={opt => {
                if (cptSuggestions[opt.code]) {
                  return (
                    <Box display="flex" alignItems="center">
                      <TipsAndUpdatesOutlinedIcon fontSize="small" className={classes.suggestedIcon} />
                      {getCptCodeLabel(opt)}
                    </Box>
                  );
                }

                return getCptCodeLabel(opt);
              }}
              groupBy={getGroupKey}
              fullWidth
              label="Add-on CPT Codes"
              color="secondary"
              inputValue={inputValue}
              setInputValue={setInputValue}
              value={null}
              setValue={(value) => {
                if (!value) {
                  return;
                }

                handleClose();
                setInputValue('');
                const values = field?.value ?? [];

                helpers.setValue([ ...values, value.id ]);
              }}
            />
          </Box>
        </ModalContent>
      </Modal>
    </Box>
  );
};


const CPTCodesForm: React.FC<CPTCodesSectionProps> = (props) => {
  const formikContext = useFormikContext<{ primaryCptCode: string; addonCptCodes: string[] }>();
  const me = useCurrentuser();

  return (
    <PsychiatryNoteSection
      title="CPT codes"
      spacingBottom={false}
      isError={!!formikContext.errors.primaryCptCode || !!formikContext.errors.addonCptCodes}
    >
      <Box px={3} pt={1} pb={3} title={`Session ID: ${props.sessionId}`}>
        <Box pb={2}>
          <Typography color="secondary" variant="overline">Primary CPT Code</Typography>
        </Box>
        <ProviderPrimaryCptCodeAutocomplete
          label="Primary CPT Code"
          name="primaryCptCode"
          medicalRole={me.medicalRole ?? ProviderMedicalRole.therapist}
          providerId={me.id}
          sessionId={props.sessionId}
        />

      </Box>
      <Box px={3} pt={1} pb={3} bgcolor="grey.50" title={`Session ID: ${props.sessionId}`}>
        <Box pb={2}>
          <Typography color="secondary" variant="overline">Add-on CPT codes</Typography>
        </Box>

        <AddonCptCodesField name="addonCptCodes" providerId={me.id} sessionId={props.sessionId} primaryCptCodeId={formikContext.values.primaryCptCode} />
      </Box>
    </PsychiatryNoteSection>
  );
};


const CPTRecommendationAlert: React.VFC<{ title: string; description: string; codes: {code: string; reason: string}[] }> = ({ title, description, codes }) => {

  return (
    <ProgressNoteAlert
      title={title}
    >
      <Typography variant="body2" color="textPrimary">{description}</Typography>
      {codes.map(c => (
        <React.Fragment key={c.code}>
          <br />
          <Typography component="strong" fontWeight={500} variant="body2" color="textPrimary">{c.code}</Typography>
          <br />
          <Typography component="li" variant="body2" color="textPrimary">{c.reason}</Typography>
        </React.Fragment>
      ))}

    </ProgressNoteAlert>
  );
};

export const CPTCodesSection: React.VFC<{
  sessionId: string;
  paperClassName?: string;
}> = ({ sessionId, paperClassName }) => {
  const me = useCurrentuser();
  const formikContext = useFormikContext<{ primaryCptCode: string; addonCptCodes: string[] }>();
  const { data: { cptCodes = [] } = {} } = useProviderCptCodesQuery(me.id);
  const { data: cptSuggestions = {} } = useSessionSuggestedCPTCodesQuery(sessionId);
  const filterOutCodes = useFilterOutCodes(me.id, formikContext.values.primaryCptCode);
  const cptCodesObj = React.useMemo(() => {
    return cptCodes.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {} as Record<string, CPTCode>);
  }, [ cptCodes ]);

  const primaryRecommendationCodes = React.useMemo(() => {
    const selectedCode = cptCodesObj[formikContext.values.primaryCptCode];

    if (!selectedCode) {
      return null;
    }

    if (selectedCode.code in cptSuggestions) {
      return [ { code: selectedCode.code, reason: cptSuggestions[selectedCode.code].message } ];
    }

    return [];
  }, [ cptCodesObj, formikContext.values.primaryCptCode, cptSuggestions ]);

  const addonRecommendationCodes = React.useMemo(() => {
    const selectedCodes = formikContext.values.addonCptCodes?.map(selectedId => cptCodesObj[selectedId]).filter(Boolean);

    return selectedCodes
      .filter(c => cptSuggestions[c.code])
      .map(c => ({ code: c.code, reason: cptSuggestions[c.code].message }))
      .filter(c => !filterOutCodes?.has(c.code));
  }, [ formikContext.values.addonCptCodes, cptCodesObj, cptSuggestions, filterOutCodes ]);

  return (
    <Box display="flex" gap="32px">
      <Box flex={3} className={paperClassName}>
        <Paper variant="outlined" square>
          <CPTCodesForm sessionId={sessionId} />
        </Paper>
      </Box>
      <Box flex={1} maxWidth="316px" display="flex" flexDirection="column" gap="16px">
        <Box minHeight="196px">
          {primaryRecommendationCodes?.length ? (
            <CPTRecommendationAlert
              title="Suggested Primary Code"
              description="Our coding expert suggests using this CPT code, based on the data in your note. If different codes more accurately reflect the session, use those instead."
              codes={primaryRecommendationCodes}
            />
          ) : null}
        </Box>
        {addonRecommendationCodes?.length ? (
          <CPTRecommendationAlert
            title="Suggested Add-On Codes"
            description="Our coding expert suggests using these CPT codes, based on the data in your note. If different codes more accurately reflect the session, use those instead."
            codes={addonRecommendationCodes}
          />
        ) : null}
      </Box>
    </Box>
  );
};
